import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, TouchableOpacity, Image, Modal, ScrollView, Animated, Dimensions, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemedText from '../common/ThemedText';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const DRAWER_WIDTH = SCREEN_WIDTH * 0.7;

type ProfileDrawerNavigationProp = StackNavigationProp<RootStackParamList>;

interface ProfileDrawerProps {
  visible: boolean;
  onClose: () => void;
}

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ visible, onClose }) => {
  const navigation = useNavigation<ProfileDrawerNavigationProp>();
  const { theme, isDark, themeMode, setThemeMode, toggleTheme } = useTheme();
  const { user } = useAuth();
  const slideAnim = useRef(new Animated.Value(-DRAWER_WIDTH)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const overlayAnim = useRef(new Animated.Value(0)).current;

  // Debug logging for user data
  useEffect(() => {
    if (visible && user) {
      console.log('🎨 [ProfileDrawer] User data:', {
        name: user.name,
        profession: user.profession,
        industry: user.industry,
        designation: (user as any).designation,
        city: user.city,
      });
    }
  }, [visible, user]);

  useEffect(() => {
    if (visible) {
      // Reset to initial values first
      slideAnim.setValue(-DRAWER_WIDTH);
      fadeAnim.setValue(0);
      overlayAnim.setValue(0);
      
      // Then animate to visible state
      Animated.parallel([
        Animated.spring(slideAnim, {
          toValue: 0,
          tension: 65,
          friction: 10,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 350,
          useNativeDriver: true,
        }),
        Animated.timing(overlayAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: -DRAWER_WIDTH,
          duration: 250,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(overlayAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, slideAnim, fadeAnim, overlayAnim]);

  const handleProfilePress = () => {
    onClose();
    navigation.navigate('Profile', {});
  };

  const handleSettingsPress = () => {
    onClose();
    navigation.navigate('Settings');
  };

  const handleAdminPress = () => {
    onClose();
    navigation.navigate('AdminNotification');
  };

  const isAdmin = (user as any)?.role === 'admin';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        <Animated.View 
          style={[
            styles.overlay,
            {
              opacity: overlayAnim,
            }
          ]}
        >
          <TouchableOpacity 
            style={StyleSheet.absoluteFill} 
            activeOpacity={1} 
            onPress={onClose}
          />
        </Animated.View>
        
        <Animated.View 
          style={[
            styles.drawer, 
            { 
              backgroundColor: theme.backgroundSecondary,
              transform: [{ translateX: slideAnim }],
            }
          ]}
        >
          <SafeAreaView edges={["top"]} style={{ flex: 1 }}>
            <Animated.View style={{ opacity: fadeAnim, flex: 1 }}>
              <ScrollView 
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                contentContainerStyle={styles.scrollContent}
              >
            {/* Close Button */}
            <TouchableOpacity 
              style={styles.closeButton} 
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close" size={28} color={theme.textSecondary} />
            </TouchableOpacity>

            {/* Profile Section */}
            <View style={styles.profileSection}>
              <View style={[styles.profilePhoto, { backgroundColor: theme.border }]}>
                {(user as any)?.photo ? (
                  <Image 
                    source={{ uri: (user as any).photo }} 
                    style={styles.profilePhotoImage}
                  />
                ) : (
                  <Ionicons name="person" size={48} color={theme.textSecondary} />
                )}
              </View>
              
              <ThemedText style={styles.name}>
                {user?.name || 'User'}
              </ThemedText>
            </View>

            {/* Basic Details */}
            <View style={[styles.detailsSection, { borderTopColor: theme.border, borderBottomColor: theme.border }]}>
              <View style={styles.detailRow}>
                <Ionicons name="location-outline" size={20} color={theme.textSecondary} />
                <ThemedText style={[styles.detailText, { color: theme.textSecondary }]}>
                  {user?.city || 'Not specified'}
                </ThemedText>
              </View>
              
              <View style={styles.detailRow}>
                <Ionicons name="briefcase-outline" size={20} color={theme.textSecondary} />
                <ThemedText style={[styles.detailText, { color: theme.textSecondary }]}>
                  {user?.industry || 'Not specified'}
                </ThemedText>
              </View>

              {(user as any)?.designation && (
                <View style={styles.detailRow}>
                  <Ionicons name="ribbon-outline" size={20} color={theme.textSecondary} />
                  <ThemedText style={[styles.detailText, { color: theme.textSecondary }]}>
                    {(user as any).designation}
                  </ThemedText>
                </View>
              )}
            </View>

            {/* Action Buttons */}
            <View style={styles.actionsSection}>
              {/* Admin Section - Only visible for admins */}
              {isAdmin && (
                <TouchableOpacity 
                  style={[styles.actionButton, styles.adminButton, { backgroundColor: theme.accent + '15' }]}
                  onPress={handleAdminPress}
                  activeOpacity={0.7}
                >
                  <Ionicons name="shield-checkmark" size={24} color={theme.accent} />
                  <ThemedText style={[styles.actionText, { color: theme.accent, fontWeight: '600' }]}>
                    Admin Notifications
                  </ThemedText>
                  <Ionicons name="chevron-forward" size={20} color={theme.accent} />
                </TouchableOpacity>
              )}

              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: theme.background }]}
                onPress={handleProfilePress}
                activeOpacity={0.7}
              >
                <Ionicons name="person-outline" size={24} color={theme.accent} />
                <ThemedText style={styles.actionText}>Business Card</ThemedText>
                <Ionicons name="chevron-forward" size={20} color={theme.textSecondary} />
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: theme.background }]}
                onPress={handleSettingsPress}
                activeOpacity={0.7}
              >
                <Ionicons name="settings-outline" size={24} color={theme.accent} />
                <ThemedText style={styles.actionText}>Settings</ThemedText>
                <Ionicons name="chevron-forward" size={20} color={theme.textSecondary} />
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: theme.background }]}
                onPress={toggleTheme}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name={themeMode === 'dark' ? 'moon-outline' : themeMode === 'system' ? 'contrast-outline' : 'sunny-outline'} 
                  size={24} 
                  color={theme.accent} 
                />
                <ThemedText style={styles.actionText}>
                  {themeMode === 'light' ? 'Light' : themeMode === 'dark' ? 'Dark' : 'System'}
                </ThemedText>
                <View style={styles.themeSegment}>
                  {(['light', 'system', 'dark'] as const).map((m) => {
                    const icon = m === 'light' ? 'sunny-outline' : m === 'dark' ? 'moon-outline' : 'contrast-outline';
                    return (
                      <TouchableOpacity
                        key={m}
                        style={[
                          styles.themeSegmentBtn,
                          { borderColor: theme.border },
                          themeMode === m && { backgroundColor: theme.accent, borderColor: theme.accent },
                        ]}
                        onPress={() => setThemeMode(m)}
                        activeOpacity={0.7}
                      >
                        <Ionicons
                          name={icon}
                          size={12}
                          color={themeMode === m ? '#fff' : theme.textSecondary}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </TouchableOpacity>
            </View>
              </ScrollView>
            </Animated.View>
          </SafeAreaView>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  drawer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  closeButton: {
    alignSelf: 'flex-end',
    padding: 12,
  },
  profileSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  profilePhoto: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: '#D4AF37',
    marginBottom: 16,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  profilePhotoImage: {
    width: '100%',
    height: '100%',
  },
  name: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 4,
    textAlign: 'center',
  },
  headline: {
    fontSize: 14,
    textAlign: 'center',
  },
  detailsSection: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailText: {
    fontSize: 14,
    marginLeft: 12,
  },
  actionsSection: {
    padding: 16,
    gap: 12,
  },
  adminButton: {
    borderWidth: 1,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    gap: 12,
  },
  actionText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
  },
  themeSegment: {
    flexDirection: 'row',
    gap: 4,
  },
  themeSegmentBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ProfileDrawer;
